import { useEffect, useMemo, useState } from "react";
import { Banknote, CreditCard, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatMoney, minorToStr, toMinor } from "@/lib/money";
import { CreditNoteBox } from "@/components/pos/CreditNoteBox";

function quickTenders(total, dp) {
  const unit = 10 ** dp;
  const steps = dp === 3 || total < 100 * unit ? [1, 5, 10, 20, 50] : [100, 500, 1000, 5000];
  const out = [];
  steps.forEach((s) => {
    const v = Math.ceil(total / (s * unit)) * s * unit;
    if (v > total && !out.includes(v)) out.push(v);
  });
  return out.slice(0, 3);
}

export function PaymentDialog({ open, onOpenChange, total: billTotal, biz, onComplete }) {
  const dp = biz.decimals;
  const [cn, setCn] = useState(null);
  const total = billTotal - (cn?.amount || 0);
  const [method, setMethod] = useState("cash");
  const [tendered, setTendered] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setMethod("cash");
      setTendered("");
      setBusy(false);
      setCn(null);
    }
  }, [open]);

  const tenderedMinor = useMemo(() => {
    try {
      return tendered.trim() ? toMinor(tendered, dp) : null;
    } catch {
      return null;
    }
  }, [tendered, dp]);

  const canComplete = !busy && (method === "card" || (tenderedMinor !== null && tenderedMinor >= total));
  const change = tenderedMinor !== null ? tenderedMinor - total : null;

  const complete = async () => {
    if (!canComplete) return;
    setBusy(true);
    const ok = await onComplete({ method, tendered: method === "cash" ? minorToStr(tenderedMinor, dp) : null, ...(cn ? { credit_note: cn.number } : {}) });
    if (!ok) setBusy(false);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "F9") {
        e.preventDefault();
        complete();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const m = (n) => formatMoney(n, dp);

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-md gap-0 overflow-hidden p-0" data-testid="payment-dialog">
        <div className="grain bg-slate-950 px-6 pb-6 pt-6 text-white">
          <DialogHeader>
            <DialogTitle className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-amber-400">Amount due</DialogTitle>
            <DialogDescription className="sr-only">Choose cash or card</DialogDescription>
          </DialogHeader>
          <div className="money mt-2 text-[40px] font-extrabold leading-none" data-testid="payment-total">
            <span className="mr-2 text-base font-semibold text-slate-400">{biz.currency}</span>{m(total)}
          </div>
        </div>
        <div className="space-y-5 p-6">
          <CreditNoteBox total={billTotal} applied={cn} onApply={setCn} dp={dp} currency={biz.currency} />
          {cn && <div className="flex justify-between text-sm font-semibold text-slate-600" data-testid="payment-remainder"><span>Remainder to pay</span><span className="money">{biz.currency} {m(total)}</span></div>}
          <div className="grid grid-cols-2 gap-2">
            {[["cash", "Cash", Banknote], ["card", "Card", CreditCard]].map(([k, l, Icon]) => (
              <button
                key={k}
                onClick={() => setMethod(k)}
                data-testid={`payment-method-${k}`}
                className={`flex h-14 items-center justify-center gap-2 rounded-xl border-2 font-semibold transition-colors ${
                  method === k ? "border-slate-900 bg-amber-50 text-slate-900" : "border-slate-200 text-slate-500 hover:border-slate-400"
                }`}
              >
                <Icon className="h-5 w-5" /> {l}
              </button>
            ))}
          </div>
          {method === "cash" ? (
            <div className="space-y-3">
              <label className="eyebrow" htmlFor="tendered">Cash tendered</label>
              <input
                id="tendered"
                autoFocus
                inputMode="decimal"
                value={tendered}
                onChange={(e) => setTendered(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && complete()}
                placeholder={minorToStr(total, dp)}
                data-testid="payment-tendered-input"
                className="money h-14 w-full rounded-xl border border-slate-300 px-4 text-2xl font-bold outline-none focus:border-slate-900 focus:ring-4 focus:ring-amber-200"
              />
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setTendered(minorToStr(total, dp))} data-testid="tender-exact" className="h-11 rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white">Exact</button>
                {quickTenders(total, dp).map((v) => (
                  <button key={v} onClick={() => setTendered(minorToStr(v, dp))} data-testid={`tender-quick-${minorToStr(v, dp)}`} className="money h-11 rounded-lg border border-slate-300 px-3.5 text-sm font-semibold text-slate-800 hover:border-slate-900">
                    {formatMoney(v, dp)}
                  </button>
                ))}
              </div>
              <div className={`flex items-baseline justify-between rounded-xl px-4 py-3 ${change !== null && change >= 0 ? "bg-emerald-50" : "bg-slate-50"}`}>
                <span className="text-sm font-semibold text-slate-600">{change !== null && change < 0 ? "Short by" : "Change due"}</span>
                <span className={`money text-2xl font-extrabold ${change !== null && change < 0 ? "text-rose-600" : "text-emerald-700"}`} data-testid="payment-change">
                  {change === null ? m(0) : m(Math.abs(change))}
                </span>
              </div>
            </div>
          ) : (
            <div className="rounded-xl bg-slate-50 px-4 py-4 text-sm text-slate-600" data-testid="payment-card-note">
              Record a card payment for the full <b className="money text-slate-900">{biz.currency} {m(total)}</b>. Run the card on your terminal, then complete.
            </div>
          )}
          <button
            onClick={complete}
            disabled={!canComplete}
            data-testid="complete-sale-button"
            className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 font-display text-lg font-bold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
          >
            {busy && <Loader2 className="h-5 w-5 animate-spin" />} Complete sale <kbd className="rounded bg-black/15 px-1.5 py-0.5 font-mono text-[10px]">F9</kbd>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
